import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab
} from '@mui/material';

import {
  Upload,
  CloudUpload,
  CheckCircle,
  Schedule,
  Assignment,
  GetApp,
  Refresh
} from '@mui/icons-material';
import { useClientConfig } from '../context/ClientConfigContext';
import ValidadoLogo from '../components/ValidadoLogo';
import usePageTitle from '../hooks/usePageTitle';

interface UploadRecord {
  id: string;
  nationalId: string;
  status: string;
  uploadDate: string;
  lastUpdated: string;
  additionalData?: any;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-tabpanel-${index}`}
      aria-labelledby={`client-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const DynamicClientPortal: React.FC = () => {
  const { config, loading, isFeatureEnabled } = useClientConfig();
  const [activeTab, setActiveTab] = useState(0);
  const [records, setRecords] = useState<UploadRecord[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    completed: 0,
    inReview: 0
  });

  // Set dynamic page title
  usePageTitle(config?.branding.companyName ? `${config.branding.companyName} Portal` : 'Client Portal');

  useEffect(() => {
    loadRecords();
  }, []);

  const loadRecords = async () => {
    try {
      // This would be replaced with actual API call based on client configuration
      const mockRecords: UploadRecord[] = [
        {
          id: 'rec_001',
          nationalId: '12345678',
          status: 'completed',
          uploadDate: '2025-01-15T10:00:00Z',
          lastUpdated: '2025-01-15T14:30:00Z'
        },
        {
          id: 'rec_002',
          nationalId: '87654321',
          status: 'in_review',
          uploadDate: '2025-01-15T11:00:00Z',
          lastUpdated: '2025-01-15T12:00:00Z'
        }
      ];
      
      setRecords(mockRecords);
      
      // Calculate stats
      const newStats = {
        total: mockRecords.length,
        pending: mockRecords.filter(r => r.status === 'pending').length,
        completed: mockRecords.filter(r => r.status === 'completed').length,
        inReview: mockRecords.filter(r => r.status === 'in_review').length
      };
      setStats(newStats);
    } catch (error) {
      console.error('Error loading records:', error);
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('csvFile', selectedFile);
      formData.append('clientEmail', 'user@company.com'); // This would come from auth context
      formData.append('companyName', config?.companyName || 'Unknown Company');
      
      // This would be the actual upload API call
      // const response = await axios.post('/api/didi/upload-csv', formData);
      
      setMessage({ type: 'success', text: 'File uploaded successfully!' });
      setUploadDialogOpen(false);
      setSelectedFile(null);
      loadRecords();
    } catch (error) {
      setMessage({ type: 'error', text: 'Upload failed. Please try again.' });
    } finally {
      setUploading(false);
    }
  };

  const downloadTemplate = () => {
    // This would download the CSV template
    window.open('/api/didi/csv-template', '_blank');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_review': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: string): React.ReactElement => {
    switch (status) {
      case 'completed': return <CheckCircle />;
      case 'in_review': return <Schedule />;
      case 'pending': return <Assignment />;
      default: return <Assignment />;
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <LinearProgress />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Loading client configuration...
        </Typography>
      </Container>
    );
  }

  if (!config) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">
          Failed to load client configuration. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with dynamic branding */}
      <Paper elevation={3} sx={{ p: 3, mb: 3, backgroundColor: config.branding.primaryColor, color: 'white' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            {config.branding.logoUrl ? (
              <img 
                src={config.branding.logoUrl} 
                alt={config.branding.companyName} 
                style={{ height: 40, marginRight: 16 }}
              />
            ) : (
              <Box sx={{ mr: 2 }}><ValidadoLogo /></Box>
            )}
            <Box>
              <Typography variant="h4" component="h1">
                {config.branding.companyName} Portal
              </Typography>
              <Typography variant="subtitle1">
                Background Verification Dashboard
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            sx={{ color: 'white', borderColor: 'white' }}
            onClick={loadRecords}
            startIcon={<Refresh />}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {/* Message Alert */}
      {message && (
        <Alert 
          severity={message.type} 
          sx={{ mb: 3 }}
          onClose={() => setMessage(null)}
        >
          {message.text}
        </Alert>
      )}

      {/* Stats Dashboard */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Records
              </Typography>
              <Typography variant="h4">
                {stats.total}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Completed
              </Typography>
              <Typography variant="h4" color="success.main">
                {stats.completed}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                In Review
              </Typography>
              <Typography variant="h4" color="warning.main">
                {stats.inReview}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Pending
              </Typography>
              <Typography variant="h4" color="info.main">
                {stats.pending}
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Dynamic Tabs based on features */}
      <Paper sx={{ width: '100%' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="Records" />
            {isFeatureEnabled('csvUpload') && <Tab label="Upload" />}
            {isFeatureEnabled('reportGeneration') && <Tab label="Reports" />}
            {isFeatureEnabled('apiAccess') && <Tab label="API" />}
          </Tabs>
        </Box>

        {/* Records Tab */}
        <TabPanel value={activeTab} index={0}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Record ID</TableCell>
                  <TableCell>National ID</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Upload Date</TableCell>
                  <TableCell>Last Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>{record.id}</TableCell>
                    <TableCell>{record.nationalId}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(record.status)}
                        label={record.status.replace('_', ' ').toUpperCase()}
                        color={getStatusColor(record.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(record.uploadDate).toLocaleDateString()}</TableCell>
                    <TableCell>{new Date(record.lastUpdated).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Upload Tab */}
        {isFeatureEnabled('csvUpload') && (
          <TabPanel value={activeTab} index={1}>
            <Box textAlign="center" py={4}>
              <CloudUpload sx={{ fontSize: 64, color: config.branding.primaryColor, mb: 2 }} />
              <Typography variant="h5" gutterBottom>
                Upload CSV File
              </Typography>
              <Typography variant="body1" color="textSecondary" mb={3}>
                Upload a CSV file with national IDs for background verification
              </Typography>
              <Box display="flex" gap={2} justifyContent="center">
                <Button
                  variant="contained"
                  onClick={() => setUploadDialogOpen(true)}
                  startIcon={<Upload />}
                  sx={{ backgroundColor: config.branding.primaryColor }}
                >
                  Upload File
                </Button>
                <Button
                  variant="outlined"
                  onClick={downloadTemplate}
                  startIcon={<GetApp />}
                >
                  Download Template
                </Button>
              </Box>
            </Box>
          </TabPanel>
        )}

        {/* Reports Tab */}
        {isFeatureEnabled('reportGeneration') && (
          <TabPanel value={activeTab} index={isFeatureEnabled('csvUpload') ? 2 : 1}>
            <Typography variant="h6" gutterBottom>
              Reports & Analytics
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Generate detailed reports and analytics for your background verification data.
            </Typography>
            {/* Report generation UI would go here */}
          </TabPanel>
        )}

        {/* API Tab */}
        {isFeatureEnabled('apiAccess') && (
          <TabPanel value={activeTab} index={isFeatureEnabled('csvUpload') && isFeatureEnabled('reportGeneration') ? 3 : isFeatureEnabled('csvUpload') || isFeatureEnabled('reportGeneration') ? 2 : 1}>
            <Typography variant="h6" gutterBottom>
              API Access
            </Typography>
            <Typography variant="body1" color="textSecondary">
              Access our API for programmatic integration with your systems.
            </Typography>
            {/* API documentation and keys would go here */}
          </TabPanel>
        )}
      </Paper>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload CSV File</DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={{ marginBottom: 16 }}
            />
            {selectedFile && (
              <Typography variant="body2" color="textSecondary">
                Selected: {selectedFile.name}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleFileUpload} 
            disabled={!selectedFile || uploading}
            variant="contained"
            sx={{ backgroundColor: config.branding.primaryColor }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button for Quick Upload */}
      {isFeatureEnabled('csvUpload') && (
        <Fab
          color="primary"
          sx={{ 
            position: 'fixed', 
            bottom: 16, 
            right: 16,
            backgroundColor: config.branding.primaryColor
          }}
          onClick={() => setUploadDialogOpen(true)}
        >
          <Upload />
        </Fab>
      )}
    </Container>
  );
};

export default DynamicClientPortal;